import { isNil, isNotNil } from '@flowio/is';
import {
  DefaultRenderParameterValue,
  isNotRedirectOnlyPaymentMethodParameter, RequestPaymentParameters, RequestPaymentReturnValue,
} from './types';
import handleCardPaymentRequest from './actions/card/handleCardPaymentRequest';
import { errorCodes } from './constants';
import handlePaymentRequestFailure from './utilities/handlePaymentRequestFailure';
import handlePaymentRequestResponse from './utilities/handlePaymentRequestResponse';
import createPaymentRequest from './services/paymentRequest/createPaymentRequest';
import { handleSdkAction } from './actions/handleSdkAction';
import validateRequestPaymentParameters from './validators/isRequestPaymentParameters';
import createPaymentRequestError from './utilities/createPaymentRequestError';
import { toCamelCaseKeysNested } from './utilities/mapKeysNested';
import postPaymentMethodData from './services/paymentRequest/postPaymentMethodData';
import { assertPaymentRequest } from './validators/assertTypes';
import getInitPaymentMethodData from './utilities/getInitPaymentMethodData';

let paymentRequest: io.flow.v0.models.PaymentRequest;

function getRenderOptionsFromParams(
  requestOptions: RequestPaymentParameters,
): Partial<DefaultRenderParameterValue> {
  if (isNotRedirectOnlyPaymentMethodParameter(requestOptions.data.paymentMethod)) {
    if (isNil(requestOptions.data.paymentMethod.render)) {
      throw new Error('fatal: data.paymentMethod.render is required and was expected.');
    }
    return {
      mountContainer: requestOptions.data.paymentMethod.render.mountContainer,
      buttons: requestOptions.data.paymentMethod.render.buttons,
    };
  }
  return {
    mountContainer: undefined,
    buttons: undefined,
  };
}

function processPaymentRequestResponse(
  requestOptions: RequestPaymentParameters,
) {
  return function processPaymentRequestResponseEffect(
    pr: io.flow.v0.models.PaymentRequest | undefined,
  ): Promise<io.flow.v0.models.PaymentRequest | undefined> {
    if (isNotNil(pr)) {
      paymentRequest = pr;
      if (isNotNil(paymentRequest.next_action)) {
        return handleSdkAction({
          pr: paymentRequest,
          ...getRenderOptionsFromParams(requestOptions),
        });
      }
    }
    return Promise.resolve(pr);
  };
}

export default function requestPayment(
  requestOptions: RequestPaymentParameters,
): Promise<RequestPaymentReturnValue | undefined> {
  try {
    validateRequestPaymentParameters(requestOptions);
  } catch (e) {
    if (e instanceof Error) {
      handlePaymentRequestFailure({
        requestOptions,
        error: createPaymentRequestError(errorCodes.INVALID_PARAMETERS, e.message),
      });
    }
    throw e;
  }
  const pm = requestOptions.data.paymentMethod;
  const paymentMethodData = getInitPaymentMethodData(requestOptions);
  return createPaymentRequest({
    requestOptions,
    paymentMethodData,
  })
    .then(processPaymentRequestResponse(requestOptions))
    .then((pr) => {
      if (isNotNil(pr) && isNotNil(requestOptions.onPaymentRequested)) {
        requestOptions.onPaymentRequested({
          request: toCamelCaseKeysNested<io.flow.v0.models.PaymentRequest>(pr),
        });
      }
      return pr;
    })
    .then((pr) => {
      if (isNotNil(pm)) {
        switch (pm.id) {
          case 'card':
            return handleCardPaymentRequest(requestOptions)
              .then((cardPaymentMethodData) => postPaymentMethodData(
                paymentRequest,
                cardPaymentMethodData,
              ));
          default:
            // APMs handled by actions further down the chain.
            return pr;
        }
      }
      // no payment method data sent with initial request.
      return pr;
    })
    .then((pr) => assertPaymentRequest(pr))
    .then<Awaited<io.flow.v0.models.PaymentRequest | undefined>>(
    processPaymentRequestResponse(requestOptions),
  )
    .then((prResponse) => {
      if (isNotNil(prResponse)) {
        handlePaymentRequestResponse(prResponse, requestOptions);
        return {
          request: toCamelCaseKeysNested<io.flow.v0.models.PaymentRequest>(prResponse),
        };
      }
      return prResponse;
    })
    .catch((e: Error) => {
      handlePaymentRequestFailure({
        requestOptions,
        error: {
          error: {
            code: errorCodes.UNEXPECTED_ERROR,
            message: `Unexpected error: ${e.message}`,
          },
        },
      });
      throw e;
    });
}
